import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import "../assets/CSS/Dashboard.css";

export default function AuditLogs() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  const [employeeFilter, setEmployeeFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const [loading, setLoading] = useState(false);

  /* ================= FETCH LOGS ================= */
  const loadLogs = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get("/api/admin/audit-logs");
      const data = res.data || [];

      setLogs(data);
      setFilteredLogs(data);

      // Create Employee Dropdown List
      const employees = [
        ...new Map(
          data.map((item) => [
            item.EmployeeID,
            { id: item.EmployeeID, name: item.EmployeeName },
          ])
        ).values(),
      ];

      setEmployeeList(employees);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  /* ================= FILTER ================= */
  useEffect(() => {
    let data = [...logs];

    if (employeeFilter) {
      data = data.filter((l) => l.EmployeeID === employeeFilter);
    }

    if (actionFilter) {
      data = data.filter((l) => l.Action === actionFilter);
    }

    setFilteredLogs(data);
  }, [employeeFilter, actionFilter, logs]);

  /* ================= TABLE HEADER STYLE ================= */
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#000",
        color: "#fff",
        fontWeight: "600",
        fontSize: "14px",
        minHeight: "45px",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
        fontSize: "14px",
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
  };

  /* ================= LOADER ================= */
  const loader = (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <div
        className="spinner-border text-primary"
        style={{ width: "3rem", height: "3rem" }}
        role="status"
      ></div>
      <div className="mt-2 text-muted">Loading logs...</div>
    </div>
  );

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      name: "Sr",
      cell: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Employee",
      cell: (row) => (
        <div>
          <div className="fw-semibold">{row.EmployeeName}</div>
          <div className="text-muted small">{row.EmployeeID}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => {
        if (row.Action === "LOGIN_SUCCESS")
          return <span className="badge bg-success">Login Success</span>;

        if (row.Action === "LOGIN_FAILED")
          return <span className="badge bg-danger">Login Failed</span>;

        if (row.Action === "DEVICE_PENDING")
          return <span className="badge bg-warning text-dark">New Device</span>;

        return <span className="badge bg-secondary">{row.Action}</span>;
      },
      sortable: true,
    },
    {
      name: "IP Address",
      selector: (row) => row.IPAddress,
    },
    {
      name: "Browser",
      cell: (row) => (
        <span style={{ fontSize: "12px" }}>
          {row.Browser ? row.Browser.substring(0, 60) : "-"}
        </span>
      ),
    },
    {
      name: "Status",
      cell: (row) => {
        if (row.Status === "SUCCESS")
          return <span className="badge bg-success">Success</span>;

        if (row.Status === "FAILED")
          return <span className="badge bg-danger">Failed</span>;

        return <span className="badge bg-warning text-dark">Pending</span>;
      },
    },
    {
      name: "Time",
      cell: (row) => new Date(row.CreatedAt).toLocaleString(),
      sortable: true,
    },
  ];

  return (
    <div className="d-flex dashboard">
      {/* Sidebar */}
      <div
        className={
          isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"
        }
      >
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* Header */}
        <Header
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="main-container p-4">
          <h3 className="mb-4 fw-bold">Audit Logs</h3>

          {/* ================= FILTER ================= */}
          <div className="card shadow-sm mb-3">
            <div className="card-body row g-3 align-items-center">

              {/* Employee Dropdown */}
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                >
                  <option value="">All Employees</option>
                  {employeeList.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.id} - {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Filter */}
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                >
                  <option value="">All Actions</option>
                  <option value="LOGIN_SUCCESS">Login Success</option>
                  <option value="LOGIN_FAILED">Login Failed</option>
                  <option value="DEVICE_PENDING">New Device</option>
                </select>
              </div>

              {/* Refresh */}
              <div className="col-md-2">
                <button className="btn btn-primary w-100" onClick={loadLogs}>
                  Refresh
                </button>
              </div>

            </div>
          </div>

          {/* ================= TABLE ================= */}
          <div className="card shadow-sm">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={filteredLogs}
                progressPending={loading}
                progressComponent={loader}
                pagination
                highlightOnHover
                striped
                responsive
                dense
                customStyles={customStyles}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}